import { getTransactionBadgeText } from './propertyLabels';

export const SITE_URL = 'https://hirebroker.co';
export const SITE_NAME = 'HireBroker';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/images/web-logo.png`;
export const DEFAULT_TITLE =
  'Verified Property Listings in Uganda | HireBroker';
export const DEFAULT_DESCRIPTION =
  'Find verified houses, apartments, and land for sale or rent in Kampala, Wakiso, Entebbe and across Uganda. HireBroker connects you with credential-checked agents and escrow-backed listings.';
export const DEFAULT_KEYWORDS =
  'property for sale Uganda, houses for rent Kampala, apartments Kampala, land for sale Wakiso, Entebbe rentals, verified real estate agents Uganda, HireBroker';

export function stripHtml(value) {
  if (!value) return '';
  return String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function truncateMeta(text, max = 158) {
  const clean = stripHtml(text);
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).replace(/\s+\S*$/, '')}…`;
}

export function withSiteName(title) {
  const raw = stripHtml(title || '').trim();
  if (!raw) return DEFAULT_TITLE;
  if (raw.toLowerCase().includes(SITE_NAME.toLowerCase())) return raw;
  return `${raw} | ${SITE_NAME}`;
}

export function citySlug(city) {
  return String(city || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function cityFromSlug(slug) {
  return String(slug || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
}

export function listingPageTitle(property) {
  const title = stripHtml(property?.title) || 'Property listing';
  const type = getTransactionBadgeText(property?.property_type) || 'Listing';
  const city = property?.city || 'Uganda';
  return `${title} | ${type} in ${city}`;
}

export function listingPageDescription(property) {
  const title = stripHtml(property?.title) || 'This property';
  const type = (getTransactionBadgeText(property?.property_type) || 'listing').toLowerCase();
  const place = [property?.address, property?.city, 'Uganda'].filter(Boolean).join(', ');
  const price = property?.price
    ? `Priced at UGX ${Number(property.price).toLocaleString('en-UG')}. `
    : '';
  const body = stripHtml(property?.description);
  if (body) {
    return truncateMeta(`${title} — ${type} in ${place}. ${price}${body}`);
  }
  return truncateMeta(
    `${title} is listed ${type} in ${place} on HireBroker. ${price}See photos, features, and contact a verified agent.`
  );
}

export function listingKeywords(property) {
  const type = getTransactionBadgeText(property?.property_type) || 'property';
  const category = property?.category?.category || 'home';
  const city = property?.city || 'Uganda';
  return [
    property?.title,
    `${category} ${type.toLowerCase()} ${city}`,
    `${type.toLowerCase()} ${city}`,
    `property ${city} Uganda`,
    'HireBroker',
  ]
    .filter(Boolean)
    .join(', ');
}

export function listingSchema(property, slugId) {
  const url = `${SITE_URL}/property/${slugId}`;
  const typeCode = String(property?.property_type ?? '').toLowerCase();
  const isRent = typeCode === '1' || typeCode === 'rent';
  const images = [];
  if (property?.title_image) images.push(property.title_image);
  (property?.gallery || []).forEach((item) => {
    const src = item?.image_url || item;
    if (src && !images.includes(src)) images.push(src);
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: stripHtml(property?.title),
    description: listingPageDescription(property),
    url,
    datePosted: property?.created_at || property?.post_created || undefined,
    image: images.length ? images : undefined,
    address: {
      '@type': 'PostalAddress',
      streetAddress: property?.address || '',
      addressLocality: property?.city || '',
      addressRegion: property?.state || '',
      addressCountry: 'UG',
    },
    geo:
      property?.latitude && property?.longitude
        ? {
            '@type': 'GeoCoordinates',
            latitude: property.latitude,
            longitude: property.longitude,
          }
        : undefined,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'UGX',
      price: parseFloat(property?.price) || 0,
      availability:
        String(property?.status) === '1'
          ? 'https://schema.org/InStock'
          : 'https://schema.org/SoldOut',
      url,
      businessFunction: isRent
        ? 'https://schema.org/LeaseOut'
        : 'https://schema.org/Sell',
    },
  };
}

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    image: DEFAULT_OG_IMAGE,
    areaServed: {
      '@type': 'Country',
      name: 'Uganda',
    },
    description: DEFAULT_DESCRIPTION,
    sameAs: [],
  };
}

export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/property-search-browse?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

export const MARKET_INTENTS = {
  'for-rent': {
    label: 'For Rent',
    h1: (city) => `Houses and apartments for rent in ${city}`,
    title: (city) => `Houses & Apartments for Rent in ${city}, Uganda`,
    description: (city) =>
      `Browse verified rental homes, apartments, and rooms in ${city}, Uganda. Compare prices, photos, and contact HireBroker agents directly.`,
    transactionType: 'rent',
    propertyType: 1,
    keywords: (city) =>
      `houses for rent ${city}, apartments for rent ${city}, rentals ${city} Uganda, HireBroker`,
  },
  'for-sale': {
    label: 'For Sale',
    h1: (city) => `Houses and property for sale in ${city}`,
    title: (city) => `Houses & Property for Sale in ${city}, Uganda`,
    description: (city) =>
      `See verified houses, apartments, and commercial property for sale in ${city}, Uganda. View prices, photos, and contact a verified HireBroker agent.`,
    transactionType: 'sell',
    propertyType: 0,
    keywords: (city) =>
      `houses for sale ${city}, property for sale ${city}, buy house ${city} Uganda, HireBroker`,
  },
  land: {
    label: 'Land',
    h1: (city) => `Land for sale in ${city}`,
    title: (city) => `Land for Sale in ${city}, Uganda`,
    description: (city) =>
      `Find plots and land for sale in ${city}, Uganda. View sizes, prices, titles, and contact verified HireBroker agents.`,
    transactionType: 'sell',
    propertyType: 0,
    keywords: (city) =>
      `land for sale ${city}, plots for sale ${city}, buy land ${city} Uganda, HireBroker`,
  },
};

export const FALLBACK_MARKETS = [
  'Kampala',
  'Wakiso',
  'Entebbe',
  'Mukono',
  'Ntinda',
  'Kira',
  'Nansana',
  'Gayaza',
  'Jinja',
  'Mbarara',
];
